import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CampaignDetail = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const t = translations[language];
  
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Fetch campaign from API
  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const response = await fetch(`${API_URL}/api/campaigns/${id}`);
        const data = await response.json();
        setCampaign(data);
      } catch (error) {
        console.error('Error fetching campaign:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCampaign();
  }, [id]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            <p className="mt-4 text-slate-600">Loading campaign...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  if (!campaign) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Campaign not found</h2>
            <Link to="/" className="text-teal-600 hover:text-teal-700">Go back home</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Get translated content
  const campaignContent = campaign.translations && campaign.translations[language] 
    ? campaign.translations[language]
    : {};
  
  const displayContent = {
    shortDescription: campaignContent.shortDescription || campaign.shortDescription,
    fullDescription: campaignContent.fullDescription || campaign.fullDescription,
    challenge: campaignContent.challenge || campaign.challenge,
    solution: campaignContent.solution || campaign.solution
  };

  if (!campaign) {
    return null; // Already handled above
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-teal-600 mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            {t.campaign.backToAll}
          </Link>
          
          <div className="mb-6 flex flex-wrap gap-3">
            <Badge variant="outline" className="text-sm px-3 py-1">{campaign.category}</Badge>
            <Badge variant="outline" className="text-sm px-3 py-1">{campaign.year}</Badge>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-4">
            {campaign.title}
          </h1>
          {campaign.subtitle && (
            <p className="text-3xl text-slate-700 font-semibold mb-6">{campaign.subtitle}</p>
          )}
          <p className="text-2xl text-teal-600 font-medium mb-6">{campaign.brand}</p>
          <p className="text-xl text-slate-600 text-justify leading-relaxed">{displayContent.fullDescription}</p>
          
          {campaign.videoLink && (() => {
            const v = campaign.videoLink.trim();
            const isEmbeddable = /facebook\.com\/plugins\/video|youtube\.com\/embed|player\.vimeo\.com\/video/i.test(v);
            if (isEmbeddable) {
              return (
                <div className="mt-8 max-w-2xl mx-auto md:mx-0">
                  <div className="relative w-full aspect-square rounded-2xl overflow-hidden shadow-lg bg-slate-100">
                    <iframe
                      src={v}
                      title={campaign.title}
                      className="absolute inset-0 w-full h-full"
                      style={{ border: 'none', overflow: 'hidden' }}
                      scrolling="no"
                      frameBorder="0"
                      allowFullScreen
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    />
                  </div>
                </div>
              );
            }
            return (
              <a
                href={v}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-6 bg-slate-900 text-white px-6 py-3 rounded-lg hover:bg-slate-800 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                {t.campaign.watchVideo}
              </a>
            );
          })()}
        </div>
      </section>

      {/* Main Image */}
      <section className="px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl mx-auto aspect-video w-full overflow-hidden rounded-2xl">
            <img 
              src={campaign.heroImage || campaign.thumbnail} 
              alt={campaign.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-12 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">{t.campaign.challenge}</h3>
            <p className="text-lg text-slate-600 leading-relaxed">{displayContent.challenge}</p>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-4">{t.campaign.solution}</h3>
            <p className="text-lg text-slate-600 leading-relaxed">{displayContent.solution}</p>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16 text-center">
            <div className="inline-flex items-center gap-2 text-teal-600 mb-4">
              <TrendingUp className="w-6 h-6" />
              <span className="text-sm font-medium uppercase tracking-wide">Impact</span>
            </div>
            <h2 className="text-4xl font-bold text-slate-900">{t.campaign.resultsTitle}</h2>
          </div>
          
          <div className="flex justify-center">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-4xl">
              {campaign.results.map((result, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-teal-600 mb-3">
                    {result.value}
                  </div>
                  <div className="text-sm md:text-base text-slate-600 font-medium leading-snug">
                    {result.metric}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Additional Images */}
      <section className="py-12 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-3xl font-bold text-slate-900 mb-8">{t.campaign.visuals}</h3>
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {campaign.images.slice(1).map((image, index) => (
              <div key={index} className="aspect-square overflow-hidden rounded-lg">
                <img 
                  src={image} 
                  alt={`${campaign.title} visual ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Navigation to other campaigns */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <Link 
            to="/#campaigns" 
            className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-lg hover:bg-teal-700 transition-all duration-300 hover:shadow-lg text-lg font-medium"
          >
            {t.campaign.viewAll}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CampaignDetail;